// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ImageTextSection } from "@/components/ImageTextSection";
import { FeatureGrid } from "@/components/FeatureGrid";
import { CtaSection } from "@/components/CtaSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import { TrendingUp, Flame, Leaf, CloudOff, Network, Orbit, Factory } from "lucide-react";

// Images
import heroImage from "@/assets/htp.png";
import ctaImage from "@/assets/hero-contact.avif";
import htp6 from "@/assets/htp6.jpg";
import rcs1 from "@/assets/rcs1.png";

export const Route = createFileRoute("/$lang/products/htp")({
  head: () => ({
    meta: [
      { title: i18n.t("products/htp:metaTitle", "High-Test Peroxide (HTP) — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/htp:metaDesc",
          "BIZU Space produces concentrated high-test peroxide (HTP) (85–99% HTP) in-house, offering a storable, non-toxic green propellant alternative to legacy toxic options.",
        ),
      },
      { property: "og:title", content: i18n.t("products/htp:metaTitle", "High-Test Peroxide (HTP) — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/htp:metaOgDesc",
          "Non-cryogenic, green aerospace propellants concentrated and stored in-house for rocket engines and suborbital missions.",
        ),
      },
      { property: "og:url", content: "/products/htp" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/htp" }],
  }),
  component: HTP,
});

function HTP() {
  const { t } = useTranslation("products/htp");

  const htpAdvantages = [
    {
      title: t("advantages.efficiencyTitle", "High-Efficiency Performance"),
      description: t("advantages.efficiencyDesc", "High specific impulse and density impulse for higher performance."),
      icon: TrendingUp,
    },
    {
      title: t("advantages.ignitionTitle", "No ignition source needed"),
      description: t("advantages.ignitionDesc",
        "HTP decomposes into water and oxygen at high temperatures, enough to ignite many fuels.",
      ),
      icon: Flame,
    },
    {
      title: t("advantages.toxicityTitle", "No-toxicity"),
      description: t("advantages.toxicityDesc",
        "Simple, easy and cost-effective handling, no harmful byproducts, breaks down into water and oxygen in the environment.",
      ),
      icon: Leaf,
    },
    {
      title: t("advantages.vaporTitle", "Low vapor pressure"),
      description: t("advantages.vaporDesc",
        "Storable at ambient temperature, HTP poses no propellant inhalation hazard and does not produce ignitable vapor clouds.",
      ),
      icon: CloudOff,
    },
    {
      title: t("advantages.dualTitle", "Dual mode operation"),
      description: t("advantages.dualDesc",
        "Engines based on HTP can be operated in dual mode - monopropellant and bipropellant.",
      ),
      icon: Network,
    },
    {
      title: t("advantages.isruTitle", "ISRU-friendly"),
      description: t("advantages.isruDesc", "Can be generated from lunar or Martian water"),
      icon: Orbit,
    },
    {
      title: t("advantages.inhouseTitle", "In-house produced"),
      description: t("advantages.inhouseDesc",
        "BIZU Space developed a concentration system that can generate HTP up to 91% and high purity peroxide of up to 99% concentration.",
      ),
      icon: Factory,
      fullWidth: true,
    },
  ];

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        title={t("heroTitle", "high-test peroxide (HTP)")}
        description={t("heroDesc",
          "High-Test Peroxide (HTP) is a highly concentrated solution (85–99%) of hydrogen peroxide (H₂O₂), utilized in satellite propulsion systems since the 1960s.",
        )}
        image={heroImage}
      />

      {/* SECTION 1: DESIGNED FOR CONTROLLED ORBITAL MISSIONS */}
      <ImageTextSection
        title={t("heritageTitle", "Operational Heritage")}
        image={rcs1}
        imagePosition="right"
        hasBorderTop={true}
        description={t("heritageDesc",
          "Beyond satellite propulsion, HTP is used by the Soyuz spacecraft docking system and as hydrogen peroxide fuel in LIFT-OFF systems (Evonik Industries), recognized as among the most reliable in the world. It is also used by the Soyuz launch vehicle to accelerate turbopumps.",
        )}
      />

      {/* HTP ADVANTAGES */}
      <section className="relative overflow-hidden bg-black py-24 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 text-center max-w-4xl mx-auto">
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl">{t("advantagesTitle", "HTP Advantages")}</h2>
          </Reveal>
          <FeatureGrid items={htpAdvantages} />
        </div>
      </section>

      <ImageTextSection
        title={
          <>
            {t("reliabilityTitle", "HTP use in rocket engines = Reliability")}
          </>
        }
        image={htp6}
        variant="container"
        hasBorderTop={true}
        hasBorderBottom={false}
        description={t("reliabilityDesc",
          "Since H2O2 is non-cryogenic, this helps to increase reliability. This propellant was highly used in the British rockets – Black Knight and Black Arrow using the Gamma engines. Of the 22 Black Knight and 4 Black Arrow launchers, involving 128 Gamma engines, there were zero engine or propulsion unit failures. The NF-104D research aircraft also successfully used the AR2-3 90% H2O2/kerosene engine to set records that still stand to this day.",
        )}
      />

      {/* SECTION 6: CALL TO ACTION */}
      <CtaSection
        image={ctaImage}
        title={t("ctaTitle", "Need high-test peroxide (HTP)?")}
        subtitle={t("ctaSubtitle",
          "BIZU Space offers concentrated HTP for experimental use and propulsion systems, ensuring reliability and consistent quality.",
        )}
        buttonText={t("ctaBtn", "Get in touch")}
        href="/contact"
      />
    </PageShell>
  );
}
